import { Injector } from '@angular/core';
import { invoke, isEqual, noop } from '@sama/micro-dash';
import { auditTime, filter, tap } from 'rxjs/operators';
import { environment } from '../../../../../../environments/environment';
import { dragMoveTestHook } from '../../../../../cross-team-shared/annotation/shared/abstract-tool';
import { AbstractDragHandler } from '../../../../../cross-team-shared/annotation/shared/tool-helpers/abstract-drag-handler';
import { EventBatchValues } from '../../../../../cross-team-shared/event-tracking/event-batch-values';
import {
  buildTrackerFromShellConnector,
  TrackingEvent,
} from '../../../../../cross-team-shared/event-tracking/event-utils';
import { getAnimationFrameScheduler } from '../../../../../cross-team-shared/misc-utils';
import { TestHookService } from '../../../../../shared/misc-services/test-hook.service';
import { Point2D } from '../../../../../team-apis/otterdam/answers/shape-typings';
import { AbstractSensorContentStore2D } from '../../../state/abstract-sensor-content-store-2d';

export abstract class AbstractDragHandler2D extends AbstractDragHandler {
  protected batchValues?: EventBatchValues;

  protected event?: TrackingEvent;
  protected shouldTrackBatchEvents = false;

  protected store: AbstractSensorContentStore2D;

  protected dedupeDragsPerImagePoint = true;
  protected ignoreNonDrags = false;

  private testHookService: TestHookService;

  private dragHappened = false;
  private lastPoint: Point2D;

  constructor(initialEvent: MouseEvent, injector: Injector) {
    super(initialEvent, injector);
    this.store = injector.get(AbstractSensorContentStore2D);
    this.testHookService = injector.get(TestHookService);

    this.lastPoint = this.getImagePoint();
  }

  override startListening() {
    super.startListening();

    this.subscribeTo(
      this.store('mouse')('imagePoint').$.pipe(
        // Throttle drag events to the speed at which the browser can repaint
        // them. Otherwise, the browser seems to skip repainting to process the
        // next event, making the UI look frozen until the user stops moving the
        // mouse (tested on Chrome 74). Technique from
        // https://developers.google.com/web/fundamentals/performance/rendering/debounce-your-input-handlers
        environment.shouldDebounceRendering
          ? auditTime(0, getAnimationFrameScheduler())
          : // for some reason, doing auditTime(0) make chrome crash in tests
            tap(noop),
        filter(
          (imagePoint) =>
            !this.dedupeDragsPerImagePoint ||
            !isEqual(imagePoint, this.lastPoint),
        ),
      ),
      this.drag.bind(this),
    );
  }

  drag() {
    this.createBatchIfNeeded();
    invoke(this.batchValues, ['startEvent']);

    const point = this.getImagePoint();
    this.onDrag(point, this.lastPoint);
    this.dragHappened = true;
    this.lastPoint = point;
    this.testHookService.run(dragMoveTestHook);

    invoke(this.batchValues, ['endEvent']);
  }

  startBatchEvent() {
    this.createBatchIfNeeded();
    if (!this.batchValues) {
      throw Error('pass an event id to track batch values');
    }
    this.batchValues.startEvent();
  }

  endBatchEvent() {
    if (!this.batchValues) {
      throw Error('pass an event id to track batch values');
    }
    this.batchValues.endEvent();
  }

  override async stopListening() {
    if (this.ignoreNonDrags && !this.dragHappened) {
      super.cancel({});
    } else {
      await super.stopListening();
    }
  }

  protected getImagePoint() {
    return this.store.state().mouse.imagePoint;
  }

  protected onMouseMove(event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
  }

  protected override onMouseUp(event: MouseEvent) {
    // Be sure to handle the move to the last point before mouse up
    const point = this.getImagePoint();
    if (!isEqual(point, this.lastPoint)) {
      this.onMouseMove(event);
      this.drag();
    }
  }

  protected getBatchValues() {
    if (!this.batchValues) {
      throw Error('there are no batch values to get');
    }

    return this.batchValues;
  }

  // Create batch values (with a batch start event) only the first time that a
  // mouse move is detected, otherwise a batch start event is sent on mouse
  // down even if there is no drag at all.
  private createBatchIfNeeded() {
    if (!this.batchValues && this.shouldTrackBatchEvents) {
      if (!this.event) {
        throw new Error('Should track batch events but has no eventId!');
      }
      this.batchValues = new EventBatchValues(
        this.event,
        buildTrackerFromShellConnector(this.shellConnector),
      );
    }
  }

  protected abstract onDrag(dragPoint: Point2D, lastPoint: Point2D): void;
}
